import { Badge } from "@/components/ui";
import { cn, taskStatus } from "@/lib/utils";
import { routes } from "@/routes/utils";
import { Trash2 } from "lucide-react";
import { useParams, useLocation } from "react-router-dom";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetSchoolTaskAssignmentQuery,
  useDeleteTaskAssignmentMutation,
  useGetTaskStatusCountsQuery
} from "@/services";
import { useAppDispatch } from "@/redux/hooks";
import { openSnackbarAction } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { todo, pause, inProgress, completed } from "@/assets";
import {
  NAME,
  SCHOOL,
  ROLE,
  PRIORITY,
  TASK_STATUS,
  ASSIGN_TO,
  START_DATE,
  END_DATE,
} from "./consts";

const useTaskAssignment = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { id, action } = useParams();

  const [deletedRow, setDeletedRow] = useState<any>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0); // same as given code
  const [pageSize, setPageSize] = useState<number>(10);

  // Prepare query params
  const queryParams = {
    page: pageNumber + 1,
    limit: pageSize,   
    orderBy: "updatedAt",        // ✅ sort field
    orderDirection: "DESC",  
    school: id,       
    ...(filterData?.[NAME] ? { taskName: filterData?.[NAME] } : {}),
    ...(filterData?.[ROLE] ? { role: filterData?.[ROLE] } : {}),
    ...(filterData?.[PRIORITY] ? { priority: filterData?.[PRIORITY] } : {}),
    ...(filterData?.[TASK_STATUS] ? { taskStatus: filterData?.[TASK_STATUS] } : {}),
    ...(filterData?.[START_DATE] ? { startDate: filterData?.[START_DATE] } : {}),
    ...(filterData?.[END_DATE] ? { endDate: filterData?.[END_DATE] } : {}),
    ...(filterData?.[ASSIGN_TO] ? { assignTo: filterData?.[ASSIGN_TO] } : {}),
  };

  // Query
  const {
    data: tasksData,
    isLoading,
    isError,
    error,
  } = useGetSchoolTaskAssignmentQuery(queryParams);

  const { mutateAsync: deleteTask, isPending: isDeleting } =
    useDeleteTaskAssignmentMutation();
  
  const {
    data: taskStatusCountsData,
    isLoading: isCountsLoading,
    isError: isCountsError,
  } = useGetTaskStatusCountsQuery();


  // Table Columns
  const columns = [
    {
      accessorKey: "name",
      header: <div>Task Name</div>,
      cell: (row: any) => (
        <span
          className="text-primary font-normal cursor-pointer"
          onClick={() => onRowClick(row.row.original)}
        >
          {String(row?.row?.original?.name || "")}
        </span>
      ),
      size: 80,
    },
    {
      accessorKey: "schoolName",
      header: <div>School Name</div>,
      accessorFn: (row: any) => String(row?.schoolName),
      size: 80,
    },
    {
      accessorKey: "priority",
      header: <div>Priority</div>,
      accessorFn: (row: any) => String(row?.priority || ""),
      size: 80,
    },
    {
      accessorKey: "startDate",
      header: <div>Start Date</div>,
      accessorFn: (row: any) => String(row?.startDate || ""),
      size: 80,
    },
    {
      accessorKey: "assignTo",
      header: <div>Assign To</div>,
      accessorFn: (row: any) => String(row?.assignTo || ""),
    },
    {
      accessorKey: "status",
      header: <div>Task Status</div>,
      cell: (row: any) => {
        const status = row?.row?.original?.status;
        const statusObj = (taskStatus as any)[status];
        return (
          <Badge
            variant="outline"
            className={cn(
              statusObj?.color,
              statusObj?.bgColor,
              "rounded-[5px] px-4 py-2 border-none"
            )}
          >
            {statusObj?.label || status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: <div>Created On</div>,
      accessorFn: (row: any) =>
        moment(row?.createdAt).format("DD-MM-YYYY") || "",
    },
    {
      accessorKey: "action",
      header: <div>Action</div>,
      cell: (row: any) => (
        <Trash2
          className="h-4 stroke-red-600 cursor-pointer"
          onClick={() => {
            setOpenDeleteDialog(true);
            setDeletedRow(row);
          }}
        />
      ),
    },
  ];

  // Data Transform
  const tableData =
    tasksData?.data?.[0]?.records?.map((record: any) => ({
      id: record._id,
      taskId: record.taskId,
      name: record.taskName,
      schoolName: record.schoolData?.[0]?.schoolName || record.school,
      priority: record.priority,
      startDate: moment(record.startDate).format("DD-MM-YYYY"),
      assignTo: record.assignToData?.[0]?.name,
      createdAt: record.createdAt,
      status: record.taskStatus,
    })) || [];

  const totalRecords = tasksData?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  


  const location = useLocation();

  const onAdd = () => {
    // navigate to add route but keep current location in state as background
    navigate(`${routes.schoolTaskAssignment}/${routes.add}`, {
      state: { background: location },
    });
  };

const onRowClick = (task: any) => {
  if (task?.taskId) {
    navigate(`${routes.schoolTaskAssignment}/${task.taskId}/${routes.edit}`, {
      state: { background: location },
    });
  }
};



  const onConfirmDelete = async () => {
  if (deletedRow?.row?.original?.taskId) {
    try {
      await deleteTask(deletedRow.row.original.taskId);

      // Show snackbar on successful delete
      dispatch(
        openSnackbarAction({
          message: "Task Assignment Deleted successfully",
          severity: "success", // as you requested
        })
      );

      // Invalidate the query to refresh table data
      queryClient.invalidateQueries({ queryKey: ["schoolTaskAssignments"] });
    } catch (error: any) {
      dispatch(
        openSnackbarAction({
          message:
            error?.response?.data?.message ||
            "An error occurred while deleting the task assignment",
          severity: "error",
        })
      );
    }
  }

  // Close dialog and reset deleted row
  setOpenDeleteDialog(false);
  setDeletedRow(null);
};


  const onCancelFilter = () => setOpenFilter(false);
  const onFilterBtnClick = () => setOpenFilter(true);

  return {
  columns,
  onAdd,
  onRowClick,
  onCancelFilter,
  onFilterBtnClick,
  openFilter,
  filterData,
  setFilterData,
  tableData,
  pageNumber,
  pageSize,
  totalPages,
  totalRecords,
  setPageNumber,
  setPageSize,
  deletedRow,
  openDeleteDialog,
  setOpenDeleteDialog,
  onConfirmDelete,
  isLoading,
  isError,
  error,
  isDeleting,
  isCountsLoading,
  isCountsError,
};

};

export default useTaskAssignment;
